import { useCallback, useEffect, useId, useState, type ComponentProps } from "react";

import { cn } from "@/lib/chillnn/cn";
import type { ChillnnStudioDriver } from "@/lib/chillnn-studio-driver";
import type { InputMaybe } from "@/lib/gql/__generated__/graphql";

import type { ForNodeType } from "@/components/features/_model/types";

import { FormErrorMessage, FormItem } from "@/components/ui/form/_base/form";
import Add from "@/components/ui/icon/add";
import { Image } from "@/components/ui/image/image";

type Props = {
	thumbnail: ForNodeType<string> | ForNodeType<InputMaybe<string> | undefined> | undefined;

	imageRepository: ComponentProps<typeof ChillnnStudioDriver>["editorProps"]["imageRepository"];
	// imageDeleter: ComponentProps<typeof ChillnnStudioDriver>["editorProps"]["imageDeleter"];
	imageUploader: ComponentProps<typeof ChillnnStudioDriver>["editorProps"]["imageUploader"];

	onSetThumbnail: (thumbnail: string) => void;
};

export const SnackThumbnailField = ({
	thumbnail,

	imageRepository,
	imageUploader, //

	onSetThumbnail: _onSetThumbnail,
}: Props) => {
	const [selectedThumbnail, setSelectedThumbnail] = useState<string | null>(thumbnail?.value ?? null);
	const [thumbnails, setThumbnails] = useState<string[]>([]);

	useEffect(() => {
		imageRepository().then((res) => {
			const images: string[] = [];
			res.map((image) => {
				images.push(image.src as string);
			});

			setThumbnails(images);
		});

		// 依存配列に imageRepository を指定する必要があるが、初期描画時に１度だけ fetch したいので、空配列を指定している
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const onSetThumbnail = useCallback(
		(thumbnail: string) => {
			setSelectedThumbnail(thumbnail);
			_onSetThumbnail(thumbnail);
		},
		[_onSetThumbnail],
	);

	const addImage = useCallback(
		async (e: React.ChangeEvent<HTMLInputElement>) => {
			const { files } = e.target;

			if (!files?.length) {
				return;
			}

			const file = files.item(0)!;

			const res = (await imageUploader(file)) as string;

			setThumbnails([res, ...thumbnails]);

			onSetThumbnail(res);
		},
		[imageUploader, onSetThumbnail, thumbnails],
	);

	const onClickThumbnail = useCallback(
		(thumbnail: string) => {
			if (selectedThumbnail === thumbnail) {
				// 選択されているサムネイルを再度選択した場合は、選択を解除する
				onSetThumbnail("");
			} else {
				// 選択
				onSetThumbnail(thumbnail);
			}
		},
		[selectedThumbnail, onSetThumbnail],
	);

	const uniqueId = useId();

	return (
		<FormItem>
			<div className="space-y-2">
				{/* 選択中のサムネイル */}
				<div className="h-[300px] max-h-[300px] w-auto rounded bg-zinc-100">
					{selectedThumbnail && <Image src={selectedThumbnail} className="h-full w-full object-contain" />}

					{!selectedThumbnail && (
						<div className="flex h-full w-full flex-col items-center justify-center gap-y-2">
							<div className="text-sm">
								<p className="text-zinc-500">新たにアップロードする画像もしくは</p>
								<p className="text-zinc-500">
									過去にアップロードした画像からサムネイルにする画像を選択してください
								</p>
							</div>
						</div>
					)}
				</div>

				<div className="flex gap-x-2 overflow-y-hidden overflow-x-scroll py-1">
					{/* 追加ボタン */}
					<label
						htmlFor={uniqueId}
						className="relative grid h-24 w-24 shrink-0 cursor-pointer place-content-center rounded border border-zinc-200 hover:bg-zinc-100"
					>
						<Add size="lg" />

						<input
							id={uniqueId}
							type="file"
							accept="image/png, image/jpeg, image/jpg, image/gif"
							onChange={addImage}
							className="sr-only absolute h-full w-full shrink-0 cursor-pointer"
						/>
					</label>

					{/* 過去にアップロードしたサムネイル */}
					{thumbnails.map((thumbnail, index) => {
						return (
							<button
								onClick={() => onClickThumbnail(thumbnail)}
								key={`${thumbnail}_${index}`}
								className={cn(
									// デフォルト
									"h-24 w-24 shrink-0 rounded border border-zinc-200 bg-zinc-100 object-cover",
									// 選択中の場合
									thumbnail === selectedThumbnail && "ring-2 ring-zinc-950",
								)}
							>
								<Image src={thumbnail} className="h-full w-full object-contain" />
							</button>
						);
					})}
				</div>
			</div>
			<FormErrorMessage>{thumbnail?.error}</FormErrorMessage>
		</FormItem>
	);
};

SnackThumbnailField.displayName = "SnackThumbnailField";
